// i18nLoader.js
import { getAccessToken } from "../lib/storeManager";
import { toI18nResource, TranslationRow } from "../lib/toI18nResource";
import { TranslationService } from "../services/APIs";
import i18n from "i18next";

// Cache to track loaded namespaces
const loadedNamespaces = new Set<string>();

export async function loadNamespace({ lng, ns }: { lng: string; ns: string }) {
  const cacheKey = `${lng}:${ns}`;
  const authToken = getAccessToken();

  // If already loaded, return early
  if (loadedNamespaces.has(cacheKey)) {
    return;
  }
  if (i18n.hasResourceBundle(lng, ns)) {
    loadedNamespaces.add(cacheKey);
    return;
  }

  // If no auth token, add empty bundle as fallback to prevent errors
  if (!authToken) {
    console.warn(
      "User is not authenticated, using empty i18n bundle as fallback"
    );
    i18n.addResourceBundle(lng, ns, {}, true, true);
    loadedNamespaces.add(cacheKey);
    return;
  }

  try {
    const res = await TranslationService.listTranslations(1, 2000);

    // Handle case where API returns undefined (e.g., 401 error)
    if (!res?.results) {
      console.warn(
        `Translation API returned no data for ${lng}:${ns}, using empty bundle`
      );
      i18n.addResourceBundle(lng, ns, {}, true, true);
      loadedNamespaces.add(cacheKey);
      return;
    }

    const filteredData = res.results.filter(
      item => item.language.toLowerCase() === lng.toLowerCase()
    );

    // If no translations found for this language, use fallback
    if (filteredData.length === 0) {
      console.warn(
        `No translations found for language: ${lng}, using fallback`
      );
      // Try to load fallback language (en) if current language is not en
      if (lng !== "en") {
        const fallbackData = res.results.filter(
          item => item.language.toLowerCase() === "en"
        );
        if (fallbackData.length > 0) {
          const data = toI18nResource(
            fallbackData as unknown as TranslationRow[]
          );
          i18n.addResourceBundle(lng, ns, data, true, true);
          loadedNamespaces.add(cacheKey);
          return;
        }
      }
      // If still no data, add empty bundle to prevent infinite retries
      i18n.addResourceBundle(lng, ns, {}, true, true);
      loadedNamespaces.add(cacheKey);
      return;
    }

    const data = toI18nResource(filteredData as unknown as TranslationRow[]);
    console.log("data", data);
    i18n.addResourceBundle(lng, ns, data, true, true);
    loadedNamespaces.add(cacheKey);
  } catch (error) {
    console.error(`Failed to load translations for ${lng}:${ns}:`, error);
    // Add empty bundle to prevent infinite retries and allow app to continue
    i18n.addResourceBundle(lng, ns, {}, true, true);
    loadedNamespaces.add(cacheKey);
  }
}

export async function ensureReady({
  lng,
  nsList,
}: {
  lng: string;
  nsList: string[];
}) {
  await Promise.all(nsList.map(ns => loadNamespace({ lng, ns })));
}
